import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Icon, Input, Dropdown } from 'semantic-ui-react'
import Layout from '~/components/Layout'
import { FormFields } from '~/components/FormFields'
import { useForm } from 'react-hook-form'
import FileUpload from '~/components/FileUpload'
import { useAuth } from '~/routes/components/AuthProvider'
import notify from '~/components/Notify'
import { useNavigate, useParams } from 'react-router-dom'
import { UsersQuery } from '~/QueryHooks/UsersQuery'
import UsersService from '~/api/services/users'
import { StyledImageContainer, StyledImageWrapper } from '~/pages/Users/User/Edit/style'
import { RouteTypes } from '~/routes/constants'
import { emailRegex } from '~/components/FormFields/constants'
import { FARMER_ROLE_SLUG, MANAGER_ROLE_SLUG } from '~/Constants'
import { handleErrors } from '~/utils/helpers'
import DairyQuery from '~/QueryHooks/DairyQuery'

const defaultImage = '/assets/default.png'

const CreateManagerUser: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { systemData, systemData: { languages } } = useAuth()
  const { id } = useParams()
  const managerRole = systemData?.roles.find(role => role.slug === MANAGER_ROLE_SLUG)

  const [correctEmail, setCorrectEmail] = useState(true)
  const [isBusy, setIsBusy] = useState(false)

  const {
    isLoading: isBusyOptions,
    data: usersOptions,
    refetch: fetchUserOptions
  } = UsersQuery.getUserOptionsData()

  const {
    data: countries,
    isLoading: isLoadingCountries,
    refetch: fetchCountries
  } = DairyQuery.getCountriesList()

  const { handleSubmit, control, setValue, watch } = useForm({
    mode: 'onSubmit'
  })

  const photoFieldUrl = watch('photo')
  const wathcMobileCodeField = watch('mobilePrefix')
  const mobileNumber = watch('mobileNumber')

  useEffect(() => {
    fetchUserOptions()
    fetchCountries()
  }, [t])

  async function onSubmit(data) {
    setIsBusy(true)
    try {
      await UsersService.storeUser({ ...data, managerId: id, roleId: managerRole.id })
      notify(t('Successfully created user'), 'success')
      navigate(RouteTypes.DAIRY_FACTORY_MANAGER_USERS)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderProfile() {
    return <>
      <Form.Field>
        {photoFieldUrl && <StyledImageWrapper>
          <img src={photoFieldUrl ? photoFieldUrl : defaultImage}></img>
          <StyledImageContainer />
          {photoFieldUrl && <Icon
            circular
            onClick={(e) => { e.preventDefault(); setValue('photo', null) }}
            name='trash'
            className='trashCan'
          />
          }
        </StyledImageWrapper>
        }
        {!photoFieldUrl && <FileUpload
          encodeFiles
          maxWidthDropZone={'100%'}
          onChange={(value) => setValue('photo', value)}
          accept={{
            'image/bmp': ['.bmp'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png']
          }}
          sizeLimitInMB={5}
        />
        }
      </Form.Field>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name='users' style={{ fontSize: '1.2em' }} />
            <div>{t('First name')}</div>
          </label>}
          rules={{
            required: {
              value: true,
              message: t('Please enter first name')
            }
          }}
          style={{ marginBottom: 10 }}
          name={'firstName'}
          placeholder={t('First name')}
        />
        <FormFields.Input
          control={control}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name='users' style={{ fontSize: '1.2em' }} />
            <div>{t('Last name')}</div>
          </label>}
          rules={{
            required: {
              value: true,
              message: t('Please enter last name')
            }
          }}
          style={{ marginBottom: 10 }}
          name={'lastName'}
          placeholder={t('Last name')}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name='at' style={{ fontSize: '1.2em' }} />
            <div>{t('Email')}</div>
          </label>}
          rules={{
            required: {
              value: true,
              message: t('Please enter email')
            },
            pattern: {
              value: emailRegex,
              message: t('Please enter valid email')
            }
          }}
          typeInput={'emailField'}
          handleEmailCheck={(correctEmail) => setCorrectEmail(correctEmail)}
          style={{ marginBottom: 10 }}
          name={'email'}
          placeholder={t('Email')}
        />
        {languages.length > 1 && <FormFields.Select
          options={systemData?.languages.map(language => ({ value: language.id, text: language.label }))}
          selection
          selectOnBlur={false}
          placeholder={t('Select Language')}
          rules={{
            required: {
              value: true,
              message: t('Please select language')
            }
          }}
          control={control}
          style={{ marginBottom: 10 }}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name={'world'} style={{ fontSize: '1.2em' }} />
            <div>{t('Language')}</div>
          </label>}
          name={'languageId'}
        />}
      </Form.Group>
      <Form.Group widths={'equal'}>
        <Form.Field style={{ paddingLeft: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name={'mobile alternate'} style={{ fontSize: '1.2em' }} />
            <div>{t('Mobile number')}</div>
          </label>
          <Input
            style={{ minWidth: 250 }}
            value={mobileNumber}
            label={<Dropdown
              scrolling
              basic
              clearable
              options={countries?.map(countryCode => ({ value: countryCode.id, text: countryCode.phoneCode, flag: countryCode.iso }))}
              selectOnBlur={false}
              onChange={(e, data) => setValue('mobilePrefix', data.value)}
              value={parseInt(wathcMobileCodeField) || ''}
              placeholder={t('Code')}
              name={'mobilePrefix'}
            />}
            onChange={(e, data) => setValue('mobileNumber', data.value)}
            labelPosition='left'
            placeholder={wathcMobileCodeField ? t('Mobile number') : t('Please select mobile code')}
            type={'number'}
            name={'mobileNumber'}
          />
        </Form.Field>
        <Form.Field />
      </Form.Group>
    </>
  }

  const role = usersOptions?.roles.find(role => role.slug === FARMER_ROLE_SLUG)?.label

  return <Layout
    segmented
    onSubmit={handleSubmit(onSubmit)}
    isBusy={isBusy || isBusyOptions || isLoadingCountries}
    disableSubmit={!correctEmail}
    action={t('Add')}
    title={t('{{role}} add', { role })}
  >
    {renderProfile()}
  </Layout>
}
export default CreateManagerUser
